import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Input,
  Button,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";

// Import Breadcrumb
import BreadCrumb from "Components/Common/BreadCrumb";
import DeleteModal from "Components/Common/DeleteModal";

import {
  useGetRiskAssessmentViewQuery,
  useGenerateRiskAssessmentPDFMutation,
} from "slices/riskAssements/riskAssementsSlice";
import {
  useGenerateReportsPDFByUuidMutation,
  useGetRiskAssesmentDownloadUidsMutation,
} from "slices/safetyApp/safetyappSlice";
import DownloadModal from "Components/Common/DownloadModal";
import { usePdfDownloader } from "Components/Hooks/usePdfDownloader";

import { AssessmentStatusId, RISK_ASSESSMENT_STATUS } from "utils/CommonUtils";
import { IsValidPermissions } from "Routes/AuthProtected";

// Redux
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "Components/Common/TableContainer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Export Modal
import Loader from "Components/Common/Loader";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";
import { Tooltip } from "react-tooltip";

const RiskAssessmentListView = () => {
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    report_status: string;
    start_date: string;
    end_date: string;
    project: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    report_status: "",
    start_date: "",
    end_date: "",
    project: "",
  });

  const {
    data: riskAssessmentData,
    isLoading: riskAssessmentloading,
    refetch: refetchriskAssessments,
    isFetching: refetchriskAssessmentFetching,
  } = useGetRiskAssessmentViewQuery(page);

  const { data: projectData } = useGetProjectsByDropDownQuery(undefined);

  const [generatePDF] = useGenerateRiskAssessmentPDFMutation();

  const [generateReportsPDFByUuid, { isLoading: isPdfLoading }] =
    useGenerateReportsPDFByUuidMutation();
  const [getPermitDownloadUids, { isLoading: isUidLoading }] =
    useGetRiskAssesmentDownloadUidsMutation();

  const { downloadState, setDownloadState, handleDownload } = usePdfDownloader({
    getPermitDownloadUids,
    generateReportsPDFByUuid,
  });

  // State for Modal and Selected Project
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  // Toggle Modal
  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  // Redirect to Home Work Permit Wizard with Project ID in the URL
  const handleCreateNewReport = () => {
    if (selectedProject) {
      navigate(`/assessment-report/${selectedProject}`);
    }
  };

  // Map Projects to Dropdown Options
  const projectOptions = useMemo(() => {
    return (
      projectData?.map((project: any) => ({
        value: project.id,
        label: `${project.project_name} (${project.project_short_name})`,
      })) || []
    );
  }, [projectData]);

  const navigate = useNavigate();

  const [customer, setCustomer] = useState<any>([]);
  const [modal, setModal] = useState<boolean>(false);

  const toggle = useCallback(() => {
    setModal((prevModal) => !prevModal);
    if (!modal) {
      setCustomer(null);
    }
  }, [modal]);

  // Add Customer
  const handleCustomerClicks = () => {
    setCustomer(null);
    toggle();
  };
  const [reportData, setReportData] = useState<any>([]);

  useEffect(() => {
    if (riskAssessmentData) {
      setReportData(riskAssessmentData);
    }
  }, [riskAssessmentData]);

  const columns = useMemo(
    () => [
      {
        Header: "Report ID",
        accessor: "assessment_id",
        filterable: false,
        Cell: ({ row }: any) => (
          <Link
            to={`/assessment-report/${row?.original?.project}/${row?.original?.assessment_id}?view_report=true`}
            className="text-primary d-inline-block edit-item-btn view-task-status-class"
          >
            {row?.original?.assessment_id}
          </Link>
        ),
      },
      {
        Header: "Project Name",
        accessor: "project_name",
        filterable: false,
      },
      {
        Header: "Supervisor",
        accessor: "supervised_by_name",
        filterable: false,
      },
      {
        Header: "Report Date",
        accessor: "report_date",
        filterable: false,
      },

      {
        Header: "Report Status",
        accessor: "report_status",
        filterable: false,
        Cell: (cell: any) => {
          const statusId = cell.value as AssessmentStatusId;
          const statusName = RISK_ASSESSMENT_STATUS[statusId] || "Unknown";
          let badgeClass = "badge bg-secondary text-dark";

          switch (statusId) {
            case 1: // Draft
              badgeClass = "badge bg-primary-subtle text-primary";
              break;
            case 2: // In Progress
            case 3: // Assigned
              badgeClass = "badge bg-info-subtle text-info";
              break;
            case 4: // Pending Review
            case 5: // In Review
              badgeClass = "badge bg-warning-subtle text-warning";
              break;
            case 6: // Completed
              badgeClass = "badge bg-success-subtle text-success";
              break;
            default:
              badgeClass = "badge bg-secondary text-dark";
          }

          return <span className={badgeClass}>{statusName}</span>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: any) => {
          const reportStatus = row?.original?.report_status_name;

          const handleView = () => {
            navigate(
              `/assessment-report/${row?.original?.project}/${row?.original?.assessment_id}?view_report=true`,
            );
          };
          const handleReportEdit = () => {
            navigate(
              `/assessment-report/${row?.original?.project}/${row?.original?.assessment_id}/`,
            );
          };

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              {IsValidPermissions(["VIEW_ONLY_RISK_ASSESSMENT"]) &&
                reportStatus === "Submitted" && (
                  <li className="list-inline-item edit" title="View">
                    <div
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={handleView}
                    >
                      <i className="ri-eye-fill fs-16"></i>
                    </div>
                  </li>
                )}
              {IsValidPermissions(["EDIT_RISK_ASSESSMENT"]) &&
                (reportStatus === "Draft" || reportStatus === "Submitted") && (
                  <li className="list-inline-item edit" title="View">
                    <div
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={handleReportEdit}
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </div>
                  </li>
                )}

              {IsValidPermissions(["DOWNLOAD_RISK_ASSESSMENT"]) &&
              (reportStatus === "Linked" || reportStatus === "Submitted") ? (
                <li className="list-inline-item edit" title="Export Permit">
                  <Link
                    to={`#`}
                    className="text-primary d-inline-block edit-item-btn export-task-status-class"
                    onClick={() => handleDownload(row?.original?.assessment_id)}
                  >
                    <i className="ri-download-fill"></i>
                  </Link>
                </li>
              ) : (
                <></>
              )}
            </ul>
          );
        },
      },
    ],
    [navigate],
  );

  // Export Modal
  const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = "Customers";
  return (
    <React.Fragment>
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".export-task-status-class"
        place="bottom"
        content="Export Reports"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".view-task-status-class"
        place="bottom"
        content="View"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".edit-task-status-class"
        place="bottom"
        content="Edit"
      />
      <div className="page-content">
        {/* <ExportCSVModal
          show={isExportCSV}
          onCloseClick={() => setIsExportCSV(false)}
          data={reportData}
          columns={columns}
          name={"Report"}
        /> */}
        <DeleteModal
          show={modal}
          onDeleteClick={handleCustomerClicks}
          onCloseClick={() => setModal(false)}
        />
        <Container fluid>
          <BreadCrumb title="Report List" pageTitle="Inspection Report" />
          <Row>
            <Col lg={12}>
              <Card id="customerList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          Risk Assessment List
                        </h5>
                      </div>
                    </div>
                    <div className="col-sm-auto">
                      <div>
                        {IsValidPermissions(["ADD_RISK_ASSESSMENT"]) && (
                          <button
                            type="button"
                            className="btn btn-success add-btn me-1"
                            id="create-btn"
                            onClick={toggleModal}
                            disabled={riskAssessmentloading}
                          >
                            <i className="ri-add-line align-bottom me-1"></i>{" "}
                            Add Report
                          </button>
                        )}{" "}
                        {/* <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setIsExportCSV(true)}
                          disabled={
                            riskAssessmentloading || reportData?.length === 0
                          }
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                          Export
                        </button> */}
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  {riskAssessmentloading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={riskAssessmentData?.results || []}
                      isLoading={refetchriskAssessmentFetching}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={page.pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                      thClass="table-light text-muted"
                      // isPermitFilter={true}
                      SearchPlaceholder="Search for Inspection Report..."
                      isPagination={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: riskAssessmentData?.count || 0,
                        next: riskAssessmentData?.next || null,
                        page_size: riskAssessmentData?.page_size || 10,
                        previous: riskAssessmentData?.previous || null,
                        current_page: riskAssessmentData?.current_page || 1,
                        total_page: riskAssessmentData?.total_page || 0,
                      }}
                      setPageState={setPageState}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
      {/* Modal for Creating New Hot Work Permit */}
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Create New Assessment</ModalHeader>
        <ModalBody>
          <Label for="projectSelect">Project</Label>
          <Input
            type="select"
            id="projectSelect"
            onChange={(e) => setSelectedProject(e.target.value)}
            value={selectedProject || ""}
          >
            <option value="">Select Project</option>
            {projectOptions.map((project: any) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={handleCreateNewReport}
            disabled={!selectedProject}
          >
            Create New Report
          </Button>
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <DownloadModal
        isOpen={downloadState.isOpen}
        toggle={() => setDownloadState((prev) => ({ ...prev, isOpen: false }))}
        status={downloadState.status}
        permitNo={downloadState.permitNo}
        errorMessage={downloadState.errorMessage}
      />
    </React.Fragment>
  );
};

export default RiskAssessmentListView;
